import { useMemo } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { ApproachExplorerDataSource, ResolvedTypeMetadata } from 'types/approachExplorer';
import { LocalizedString } from 'types/context';
import { getApproach, getArcTemplate } from '../api/marketplaceApi';

function getLocalizedValue(
  field: LocalizedString | string | null | undefined,
  locale = 'en-US'
): string | null {
  if (!field) return null;
  if (typeof field === 'string') return field;
  return field.translations?.[locale] ?? field.translations?.['en-US'] ?? field.default ?? null;
}

/**
 * Returns an `ApproachExplorerDataSource` backed by the platform API clients.
 * Returns `null` when API clients or organizationId are not yet available.
 */
export function useApproachExplorerDataSource(): ApproachExplorerDataSource | null {
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const schemaClient = apiClients?.schemaClient ?? null;

  return useMemo(() => {
    if (!schemaClient || !organizationId) return null;

    const dataSource: ApproachExplorerDataSource = {
      fetchApproach: async (systemName, version?) => {
        const response = await getApproach(
          schemaClient,
          systemName,
          { version, includeDraft: true }
        );

        const approachData = response.approach;
        const resolvedVersion =
          approachData.version ?? approachData.latestVersion ?? null;
        const definition = resolvedVersion?.definition;

        if (!definition) {
          throw new Error(`Approach "${systemName}" has no definition`);
        }

        // Resolve arc template if the definition references one
        let arcTemplate = undefined;
        const arcSystemName = definition.arc?.templateSystemName;
        if (arcSystemName) {
          try {
            const template = await getArcTemplate(
              schemaClient,
              arcSystemName
            );
            if (template) {
              arcTemplate = template;
            }
          } catch {
            // Arc template fetch is non-critical
          }
        }

        return {
          name: getLocalizedValue(approachData.name) ?? systemName,
          description: getLocalizedValue(approachData.description),
          definition,
          arcTemplate,
        };
      },

      resolveTypes: async (systemNames) => {
        const result = new Map<string, ResolvedTypeMetadata>();

        await Promise.all(
          systemNames.map(async (systemName) => {
            try {
              const schemas = await schemaClient.get('/schemas', {
                params: { systemName },
              });

              const schemaData = Array.isArray(schemas)
                ? schemas[0]
                : schemas?.data?.[0];

              if (schemaData?.name) {
                const metadata: ResolvedTypeMetadata = {
                  displayName: schemaData.name,
                  description: schemaData.description ?? undefined,
                  avatar: schemaData.avatar || undefined,
                  properties: Array.isArray(schemaData.properties) ? schemaData.properties : undefined,
                  perspectives: Array.isArray(schemaData.perspectives) ? schemaData.perspectives : undefined,
                  useCases: Array.isArray(schemaData.use_case_tags) ? schemaData.use_case_tags : undefined,
                };
                result.set(systemName, metadata);
              }
            } catch {
              // Individual failure doesn't break the batch
            }
          })
        );

        return result;
      },

      resolveContexts: async (systemNames) => {
        const result = new Map<string, { displayName: string; description?: string }>();

        await Promise.all(
          systemNames.map(async (systemName) => {
            try {
              const response = await schemaClient.get(
                `/contexts/system_name/${encodeURIComponent(systemName)}`
              );

              const contextData = response?.data ?? response;
              const displayName = getLocalizedValue(contextData?.name);
              const description = getLocalizedValue(contextData?.description);

              if (displayName) {
                result.set(systemName, {
                  displayName,
                  description: description || undefined,
                });
              }
            } catch {
              // Individual failure doesn't break the batch
            }
          })
        );

        return result;
      },
    };

    return dataSource;
  }, [schemaClient, organizationId]);
}
